import { SearchModel } from './common';

/**
 * Tipos para o módulo de Produção (atelier de cabides)
 *
 * ProductionActivity.category:
 *   0 = Preparação | 1 = Flocagem | 2 = Corte | 3 = Personalização
 *   4 = Montagem/Colagem | 5 = Acabamento/Outros
 */

// ============================================================
// Atividades de Produção
// ============================================================

export const PRODUCTION_ACTIVITY_CATEGORIES = [
  { value: 0, label: 'Preparação' },
  { value: 1, label: 'Flocagem' },
  { value: 2, label: 'Corte' },
  { value: 3, label: 'Personalização' },
  { value: 4, label: 'Montagem/Colagem' },
  { value: 5, label: 'Acabamento/Outros' },
] as const;

export const getActivityCategoryLabel = (category: number): string => {
  const option = PRODUCTION_ACTIVITY_CATEGORIES.find((c) => c.value === category);
  return option ? option.label : 'Desconhecida';
};

export interface ProductionActivity {
  id: number;
  code: string;
  name: string;
  category: number;
  unit?: string;
  estimated_minutes_per_unit?: number;
  notes?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

export interface ProductionActivityFormData {
  code: string;
  name: string;
  category: number;
  unit?: string;
  estimated_minutes_per_unit?: number;
  notes?: string;
  is_active?: boolean;
}

export interface ProductionActivitySearchModel extends SearchModel {
  category?: number;
  is_active?: boolean;
}

// ============================================================
// Colaboradores de Produção
// ============================================================

export interface ProductionEmployee {
  id: number;
  name: string;
  notes?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

export interface ProductionEmployeeFormData {
  name: string;
  notes?: string;
  is_active?: boolean;
}

export interface ProductionEmployeeSearchModel extends SearchModel {
  is_active?: boolean;
}

// ============================================================
// Lista de Materiais (BOM)
// ============================================================

/**
 * Tipos de matéria-prima que podem ser pai de uma BOM
 * (subconjunto do MaterialType de rawMaterial)
 */
export const BOM_PARENT_MATERIAL_TYPE_LABELS: Record<number, string> = {
  2: 'Composto',
  3: 'Semiacabado',
};

export interface BomItem {
  id: number;
  bill_of_material_id: number;
  component_raw_material_id: number;
  quantity: number;
  scrap_percent: number;
  notes?: string;
  component_code?: string;
  component_description?: string;
  component_unit?: string;
  component_cost?: number;
  component_material_type?: number;
}

export interface BillOfMaterial {
  id: number;
  product_id?: number;
  raw_material_id?: number;
  output_quantity: number;
  notes?: string;
  is_active: boolean;
  parent_code?: string;
  parent_description?: string;
  items: BomItem[];
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

export interface BomItemFormData {
  component_raw_material_id: number;
  quantity: number;
  scrap_percent?: number;
  notes?: string;
}

export interface BillOfMaterialCreateData {
  product_id?: number;
  raw_material_id?: number;
  output_quantity?: number;
  notes?: string;
  items: BomItemFormData[];
}

export interface BillOfMaterialUpdateData {
  output_quantity?: number;
  notes?: string;
  is_active?: boolean;
  /** Substitui a lista completa de componentes */
  items: BomItemFormData[];
}

export interface BillOfMaterialSearchModel extends SearchModel {
  product_id?: number;
  raw_material_id?: number;
  is_active?: boolean;
}

// ============================================================
// Apontamentos de Produção
// ============================================================

export interface ProductionActivityLog {
  id: number;
  activity_id: number;
  employee_id: number;
  production_order_id?: number;
  /** Data do apontamento (ISO) */
  log_date: string;
  quantity_ok: number;
  quantity_defect: number;
  time_spent_minutes?: number;
  notes?: string;
  // Campos auxiliares para exibição
  activity_code?: string;
  activity_name?: string;
  activity_category?: number;
  employee_name?: string;
  production_order_code?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

export interface ProductionActivityLogCreateData {
  activity_id: number;
  employee_id: number;
  production_order_id?: number;
  /** AAAA-MM-DD */
  log_date: string;
  quantity_ok: number;
  quantity_defect?: number;
  time_spent_minutes?: number;
  notes?: string;
}

/** Atualização: todos os campos opcionais (log_date em AAAA-MM-DD) */
export type ProductionActivityLogUpdateData = Partial<ProductionActivityLogCreateData>;

export type ProductionActivityLogFormData = ProductionActivityLogCreateData;

export interface ProductionActivityLogSearchModel extends SearchModel {
  /** AAAA-MM-DD */
  date_from?: string;
  /** AAAA-MM-DD */
  date_to?: string;
  employee_id?: number;
  activity_id?: number;
  production_order_id?: number;
}

// ============================================================
// Ordens de Produção
// ============================================================

/**
 * Status da Ordem de Produção
 *   0 = Planejada | 1 = Em Produção | 2 = Concluída | 3 = Cancelada
 */
export const PRODUCTION_ORDER_STATUS: Record<
  number,
  { label: string; badgeClass: string }
> = {
  0: { label: 'Planejada', badgeClass: 'bg-slate-100 text-slate-700' },
  1: { label: 'Em Produção', badgeClass: 'bg-blue-100 text-blue-800' },
  2: { label: 'Concluída', badgeClass: 'bg-green-100 text-green-800' },
  3: { label: 'Cancelada', badgeClass: 'bg-red-100 text-red-800' },
};

export const PRODUCTION_ORDER_STATUS_OPTIONS = [
  { value: 0, label: 'Planejada' },
  { value: 1, label: 'Em Produção' },
  { value: 2, label: 'Concluída' },
  { value: 3, label: 'Cancelada' },
] as const;

export const getProductionOrderStatusLabel = (status: number): string =>
  PRODUCTION_ORDER_STATUS[status]?.label || 'Desconhecido';

export const getProductionOrderStatusBadgeClass = (status: number): string =>
  PRODUCTION_ORDER_STATUS[status]?.badgeClass || 'bg-slate-100 text-slate-700';

export interface ProductionOrderItem {
  id: number;
  production_order_id: number;
  product_id?: number;
  raw_material_id?: number;
  quantity_planned: number;
  quantity_produced: number;
  quantity_lost: number;
  notes?: string;
  target_code?: string;
  target_description?: string;
}

export interface ProductionOrderSalesOrder {
  production_order_id: number;
  order_id: number;
  order_num_erp?: string;
  order_num_ecommerce?: string;
  order_date?: string;
  customer_name?: string;
}

export interface ProductionOrder {
  id: number;
  code: string;
  description?: string;
  flock_color?: string;
  status: number;
  planned_start_date?: string;
  planned_end_date?: string;
  started_at?: string;
  completed_at?: string;
  notes?: string;
  items: ProductionOrderItem[];
  sales_orders: ProductionOrderSalesOrder[];
  items_count?: number;
  total_planned?: number;
  total_produced?: number;
  total_lost?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

export interface ProductionOrderItemCreateData {
  /** product_id XOR raw_material_id */
  product_id?: number;
  raw_material_id?: number;
  quantity_planned: number;
  notes?: string;
}

export interface ProductionOrderCreateData {
  /** Auto-gerado (ex.: 'OP-2026-001') se vazio */
  code?: string;
  description?: string;
  flock_color?: string;
  /** AAAA-MM-DD */
  planned_start_date?: string;
  /** AAAA-MM-DD */
  planned_end_date?: string;
  notes?: string;
  items: ProductionOrderItemCreateData[];
  sales_order_ids?: number[];
}

/** Atualização: mesmo shape sem code; items e sales_order_ids substituem tudo */
export type ProductionOrderUpdateData = Omit<ProductionOrderCreateData, 'code'>;

export interface ProductionOrderCompleteItemData {
  production_order_item_id: number;
  quantity_produced: number;
  quantity_lost: number;
}

export interface ProductionOrderCompleteData {
  items: ProductionOrderCompleteItemData[];
  /** Baixa componentes pelo consumo da BOM (default true) */
  consume_materials?: boolean;
  notes?: string;
}

export interface ProductionOrderSearchModel extends SearchModel {
  status?: number;
  /** AAAA-MM-DD */
  date_from?: string;
  /** AAAA-MM-DD */
  date_to?: string;
}

// ============================================================
// Relatório: Produtos a Fabricar
// ============================================================

export interface PendingManufacturingOrder {
  order_id: number;
  order_num_erp?: string;
  order_num_ecommerce?: string;
  order_date?: string;
  customer_name?: string;
  quantity: number;
}

export interface PendingManufacturingProduct {
  product_id: number;
  sku: string;
  description?: string;
  total_quantity: number;
  orders_count: number;
  oldest_order_date?: string;
  orders: PendingManufacturingOrder[];
}

// ============================================================
// Relatórios de Produção
// ============================================================

/** Filtro de período comum aos relatórios (AAAA-MM-DD) */
export interface ReportPeriodFilter {
  /** AAAA-MM-DD */
  date_from?: string;
  /** AAAA-MM-DD */
  date_to?: string;
}

/** Linha do relatório de produtividade (colaborador × atividade) */
export interface ProductivityRow {
  employee_id: number;
  employee_name: string;
  activity_id: number;
  activity_code: string;
  activity_name: string;
  activity_category: number;
  estimated_minutes_per_unit?: number;
  entries: number;
  quantity_ok: number;
  quantity_defect: number;
  time_spent_minutes: number;
  defect_percent: number;
  pieces_per_hour?: number;
  estimated_minutes?: number;
  efficiency_percent?: number;
}

export interface ProductivityReportFilters extends ReportPeriodFilter {
  employee_id?: number;
  activity_id?: number;
}

/** Linha do balanço de materiais (fluxos físicos × estoque registrado) */
export interface MaterialBalanceRow {
  raw_material_id: number;
  code: string;
  description: string;
  unit?: string;
  material_type: number;
  qty_received: number;
  qty_production_entries: number;
  qty_consumed_production: number;
  qty_lost_production: number;
  qty_sales_out: number;
  qty_adjustments_in: number;
  qty_adjustments_out: number;
  has_balance_count: boolean;
  net_period_delta: number;
  net_after_period: number;
  current_stock: number;
  min_stock_quantity?: number;
  initial_stock: number;
  theoretical_end: number;
  end_stock: number;
  discrepancy: number;
}

/** Produto a produzir (demanda de pedidos em digitação) */
export interface MaterialRequirementsProduct {
  product_id: number;
  sku: string;
  description?: string;
  demand: number;
  stock_available: number;
  net_to_produce: number;
  has_bom: boolean;
}

/**
 * Material necessário para atender a demanda.
 * has_bom = true → item produzido internamente (semiacabado/composto) → 'Produzir'
 * has_bom = false → item adquirido de terceiros → 'Comprar'
 */
export interface MaterialRequirementsMaterial {
  raw_material_id: number;
  code: string;
  description: string;
  unit?: string;
  material_type: number;
  gross_required: number;
  stock_available: number;
  net_required: number;
  has_bom: boolean;
  min_stock_quantity?: number;
  max_stock_quantity?: number;
}

export interface MaterialRequirementsResult {
  products: MaterialRequirementsProduct[];
  materials: MaterialRequirementsMaterial[];
  missing_bom_products?: string[];
}

/** Linha do relatório de custo por Ordem de Produção concluída */
export interface OrderCostRow {
  production_order_id: number;
  code: string;
  description?: string;
  flock_color?: string;
  completed_at?: string;
  quantity_produced: number;
  quantity_lost: number;
  material_cost: number;
  labor_minutes: number;
  material_cost_per_unit?: number;
  minutes_per_unit?: number;
  loss_rate_percent: number;
}
